/*
 *  Licensed under the EUPL, Version 1.2 or – as soon they will be approved by
the European Commission - subsequent versions of the EUPL (the "Licence");
You may not use this work except in compliance with the Licence.
You may obtain a copy of the Licence at:

  https://joinup.ec.europa.eu/software/page/eupl

Unless required by applicable law or agreed to in writing, software
distributed under the Licence is distributed on an "AS IS" basis,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the Licence for the specific language governing permissions and
limitations under the Licence. */

import DirectionsBikeIcon from "@mui/icons-material/DirectionsBike";
import LocalParkingIcon from "@mui/icons-material/LocalParking";
import { Box, Chip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useRef, useState } from "react";
import { useIntl } from "react-intl";
import type { MarkerDragEvent } from "react-map-gl/maplibre";
import { Marker } from "react-map-gl/maplibre";
import { StopPlaceActions } from "../../../../actions";
import { moveElementIfAllowed } from "../../../../actions/StopPlaceActions.modern";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import {
  buildElementStatusByIndex,
  ElementStatusMapBadge,
  isPendingStatus,
  useElementStatusEnabled,
} from "../../Shared/ElementStatus";
import type { CrosshairSetting } from "../crosshair";
import { DragCrosshair, getCrosshairPreference } from "../crosshair";
import { useCanEditOnMap } from "../hooks/useCanEditOnMap";
import { useCheckedMove } from "../hooks/useCheckedMove";
import { useMapCurrentStop } from "../hooks/useMapCurrentStop";
import { useMarkerScale } from "../hooks/useMarkerScale";
import { MarkerPopup } from "./MarkerPopup";
import type { FocusedElement, LatLng, MapParking } from "./types";
import { MarkerCoordinatesDialog } from "./MarkerCoordinatesDialog";

const PARKING_SIZE = 30;
/* Parking is the one map element drawn as a square. Stop places, quays,
   boarding positions and neighbours are all circles, so shape — not just
   colour — tells parking apart at a glance, the way a road sign does. Lightly
   rounded so it reads as a sign rather than a hard box; scaled with the marker
   so the proportion holds at every zoom. */
const PARKING_CORNER_RADIUS = 6;

interface ParkingMarkerItemProps {
  parking: MapParking;
  index: number;
  disabled: boolean;
  focused: boolean;
  /** True when the parking is locally added or edited and not yet saved. */
  isPending: boolean;
  /** The stop's position, which typed coordinates are checked against. */
  stopLocation?: LatLng;
}

const ParkingMarkerItem = ({
  parking,
  index,
  disabled,
  focused,
  isPending,
  stopLocation,
}: ParkingMarkerItemProps) => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const [popupAnchor, setPopupAnchor] = useState<HTMLElement | null>(null);
  const [isEditingCoordinates, setIsEditingCoordinates] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const crosshairRef = useRef<CrosshairSetting>("none");
  const scale = useMarkerScale();
  const isStatusEnabled = useElementStatusEnabled();
  const { pendingPosition, moveTo } = useCheckedMove((position) =>
    dispatch(
      moveElementIfAllowed({ markerIndex: index, type: "parking" }, position),
    ),
  );

  if (!parking.location) return null;

  const [lat, lng] = pendingPosition ?? parking.location;
  // NeTEx answers "which vehicle" with the vehicle types, not the parking
  // type. A park-and-ride parking can be for bicycles.
  const isBike = parking.isForPedalCycle ?? false;
  const titleFallbackKey = isBike
    ? "parking_item_title_bikeParking"
    : "parking_item_title_parkAndRide";
  const title = parking.name || formatMessage({ id: titleFallbackKey });

  const handleDragStart = () => {
    crosshairRef.current = getCrosshairPreference();
    setIsDragging(true);
  };

  const handleDragEnd = (event: MarkerDragEvent) => {
    setIsDragging(false);
    moveTo([event.lngLat.lat, event.lngLat.lng]);
  };

  const showCrosshair = isDragging && crosshairRef.current !== "none";

  return (
    <>
      <Marker
        latitude={lat}
        longitude={lng}
        draggable={!disabled}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        anchor="center"
      >
        {showCrosshair ? (
          <DragCrosshair
            type={crosshairRef.current as Exclude<CrosshairSetting, "none">}
          />
        ) : (
          <Box
            onClick={(e) => {
              dispatch(
                StopPlaceActions.setElementFocus(
                  index,
                  isBike ? "bikeParking" : "parkAndRide",
                ),
              );
              setPopupAnchor(e.currentTarget);
            }}
            sx={(theme) => ({
              // Containing block for ElementStatusMapBadge — see QuayMarkers.
              position: "relative",
              width: Math.round(PARKING_SIZE * scale),
              height: Math.round(PARKING_SIZE * scale),
              borderRadius: `${Math.round(PARKING_CORNER_RADIUS * scale)}px`,
              // Focus is shown by the ring and scale only, never by recolouring —
              // matches QuayMarkers so the base colour always identifies the type.
              bgcolor: "info.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              border: "2px solid",
              borderColor: "background.paper",
              boxShadow: focused
                ? `0 0 0 2px ${alpha(theme.palette.warning.main, 0.5)}, 0 2px 6px rgba(0,0,0,0.4)`
                : "0 2px 4px rgba(0,0,0,0.35)",
              transform: focused ? "scale(1.2)" : "none",
              transition: "all 0.15s",
              "&:hover": { transform: "scale(1.25)" },
            })}
          >
            {isBike ? (
              <DirectionsBikeIcon
                sx={{
                  fontSize: `${1.1 * scale}rem`,
                  color: "info.contrastText",
                }}
              />
            ) : (
              <LocalParkingIcon
                sx={{
                  fontSize: `${1.1 * scale}rem`,
                  color: "info.contrastText",
                }}
              />
            )}
            <ElementStatusMapBadge
              visible={isStatusEnabled && isPending}
              scale={scale}
            />
          </Box>
        )}
      </Marker>

      {/* Dismissing the popup (e.g. clicking elsewhere on the map) only hides the
          popup bubble — it must not clear focus, since a click "elsewhere" is
          often the user placing a new element via AddElementFab, which needs
          focus to stay put. */}
      <MarkerPopup
        anchorEl={popupAnchor}
        onClose={() => setPopupAnchor(null)}
        title={title}
        id={parking.id}
        lat={lat}
        lng={lng}
        onEditCoordinates={
          disabled
            ? undefined
            : () => {
                setPopupAnchor(null);
                setIsEditingCoordinates(true);
              }
        }
      >
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block", mt: 0.5 }}
        >
          {formatMessage({
            id: isBike
              ? "parking_item_title_bikeParking"
              : "parking_item_title_parkAndRide",
          })}
        </Typography>
        {parking.totalCapacity != null && (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block" }}
          >
            {formatMessage({ id: "total_capacity" })}: {parking.totalCapacity}
          </Typography>
        )}
        {parking.hasExpired && (
          <Chip
            label={formatMessage({ id: "expired" })}
            size="small"
            color="error"
            sx={{ mt: 0.5, height: 18, fontSize: "0.65rem" }}
          />
        )}
      </MarkerPopup>
      <MarkerCoordinatesDialog
        open={isEditingCoordinates}
        lat={lat}
        lng={lng}
        onClose={() => setIsEditingCoordinates(false)}
        onSubmit={moveTo}
        reference={
          stopLocation ? { position: stopLocation, kind: "stop" } : undefined
        }
      />
    </>
  );
};

export const ParkingMarkers = () => {
  const current = useMapCurrentStop();
  const canEditOnMap = useCanEditOnMap(current);
  const focusedElement = useAppSelector(
    (state) =>
      (state as any).mapUtils?.focusedElement as FocusedElement | undefined,
  );
  const isMergingStop = useAppSelector(
    (state) => !!(state as any).stopPlace?.mergeStopDialog?.isOpen,
  );

  const originalParking = useAppSelector(
    (state) => (state.stopPlace as any).originalCurrent?.parking,
  );

  if (!current?.parking?.length) return null;
  if (isMergingStop) return null;

  const parkingStatuses = buildElementStatusByIndex(
    current.parking,
    originalParking,
  );

  return (
    <>
      {current.parking.map((parking, index) => (
        <ParkingMarkerItem
          key={parking.id || index}
          parking={parking}
          index={index}
          disabled={!canEditOnMap}
          stopLocation={current.location}
          focused={
            (focusedElement?.type === "parkAndRide" ||
              focusedElement?.type === "bikeParking") &&
            focusedElement?.index === index
          }
          isPending={isPendingStatus(parkingStatuses[index] ?? "unchanged")}
        />
      ))}
    </>
  );
};
